<template>
  <div :class="['content', programStyle]">
    <Periods />
    <div class="bottom">
      <div class="header">
        <div class="title">{{ $t('pillar-details') }}</div>
        <div class="flex-row">
          <button style="margin-right: 15px" @click="generateReport">{{ $t('save-as-pdf') }}</button>
          <button v-if="showCollapse && pillarDetails.length > 1" class="small-btn" @click="collapseAll(pillarDetails)">
            {{ $t('collapse-all') }}
          </button>
          <button class="small-btn" v-if="!showCollapse && pillarDetails.length > 1" @click="expandAll(pillarDetails)">
            {{ $t('expand-all') }}
          </button>
        </div>
      </div>
      <VueHtml2pdf
        :show-layout="false"
        :float-layout="true"
        :enable-download="true"
        :preview-modal="true"
        filename="dealer-info"
        :pdf-quality="2"
        :manual-pagination="true"
        pdf-format="a3"
        pdf-orientation="landscape"
        pdf-content-width="100%"
        @progress="onProgress($event)"
        @hasDownloaded="hasDownloaded($event)"
        ref="html2Pdf"
      >
        <section slot="pdf-content">
          <div class="pillars">
            <div class="pdf-header">
              <div class="left">
                <span class="title">{{ selectedDealer.dealerName }} </span>
                <span>{{ programBreadcrumb }}</span>
              </div>
              <div class="right">
                <div>
                  <span>{{ $t('bc-selecteddealer', [selectedDealer.businessCenterAbbreviation]) }} </span>
                  <span>{{ $t('dealer-code-selecteddealer', [selectedDealer.entity]) }} </span>
                  <span>{{ $t('sales-district-selecteddealer', [selectedDealer.salesDistrict]) }} </span>
                  <span>{{ $t('service-district-selecteddealer', [selectedDealer.serviceDistrict]) }} </span>
                  <span>{{ $t('sales-group-selecteddealer', [selectedDealer.groupSize]) }}</span>
                </div>
              </div>
            </div>
            <div class="pillar-row" v-for="pillar in pillarDetails" :key="pillar.pillarId">
              <div class="pillar-row-header">
                <div class="Performance-pdf" v-if="pillar.pillarIconSet === 'CFAFE Performance'">
                  <Performance :class="pillar.pillarStatus" /><span>{{ pillar.pillarTitle }}</span>
                </div>
                <div class="Training" v-else-if="pillar.pillarIconSet === 'CFAFE Training'">
                  <Training :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
                </div>
                <div class="Process" v-else-if="pillar.pillarIconSet === 'CFAFE Process'">
                  <Process :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
                </div>
                <div class="Facilities" v-else-if="pillar.pillarIconSet === 'CFAFE Facility'">
                  <Facilities :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
                </div>
                <div class="EmployeeSurvey" v-else-if="pillar.pillarIconSet === 'CFAFE Survey'">
                  <EmployeeSurvey :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
                </div>
                <div class="Process" v-else><Process :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}</div>
              </div>
              <div v-if="pillar.show" class="pillar-content">
                <div class="pillar">
                  <div class="pillar-details html2pdf__page-break pdf" v-for="row in pillar.rows" :key="row.key">
                    <div class="pillar-row-details">
                      <div class="pillar-title" v-if="row.pillarRowTitle">{{ row.pillarRowTitle }}:</div>
                      <div class="dial-container" v-for="dialRow in row.dials" :key="dialRow.key">
                        <div class="smmt" v-if="dialRow.dialTypeId === 'SMMT'">
                          <IconProgress
                            :progress="dialRow.smmtProgress"
                            :targets="dialRow.smmtTargets"
                            :color="dialRow.dialColor"
                          />
                          <div class="smmtInner-pdf" v-if="dialRow.dialResultText">
                            {{ dialRow.dialResultText }}
                          </div>
                        </div>
                        <div class="smmt" v-else-if="dialRow.dialTypeId === 'FDMT'">
                          <IconProgress
                            :progress="dialRow.smmtProgress"
                            :targets="dialRow.smmtTargets"
                            :color="dialRow.dialColor"
                          />
                          <div class="smmtInner" v-if="dialRow.dialResultText">
                            {{ dialRow.dialResultText }}
                          </div>
                        </div>
                        <div
                          v-else-if="dialRow.dialResultText !== 'EMPTY' && dialRow.dialName !== 'OR'"
                          :class="['circle', dialRow.dialColor]"
                        >
                          <div class="inner" v-if="dialRow.dialResultText">{{ dialRow.dialResultText }}</div>
                          <div class="inner-graphic" v-if="dialRow.dialResultGraphic">
                            <img
                              :class="dialRow.dialResultGraphic"
                              alt=""
                              :src="getDialImage(dialRow.dialResultGraphic)"
                            />
                          </div>
                        </div>
                        <div v-if="dialRow.dialResultText !== 'EMPTY' && dialRow.dialName !== 'OR'" class="dialId">
                          {{ dialRow.dialName }}
                        </div>
                        <div v-if="dialRow.dialName === 'OR'" class="dialId or">{{ dialRow.dialName }}</div>
                      </div>
                    </div>
                    <template v-for="(dialTables, index) in row.dialData">
                      <div
                        :key="`dialTables-${index}`"
                        :class="['pillar-row-table', dialTables[0].dialId, 'pdf']"
                        v-if="dialTables[0].dialType !== 'SNM'"
                      >
                        <div class="pillar-row-inner" v-for="(dialInfo, index) in dialTables" :key="`object-${index}`">
                          <DialInfo :dialInfo="dialInfo" />
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </VueHtml2pdf>
      <div class="pillars">
        <div class="pillar-row" v-for="pillar in pillarDetails" :key="pillar.pillarId">
          <div class="pillar-row-header" @click="pillar.show = !pillar.show">
            <div class="Performance" v-if="pillar.pillarIconSet === 'CFAFE Performance'">
              <Performance :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
            </div>
            <div class="Training" v-else-if="pillar.pillarIconSet === 'CFAFE Training'">
              <Training :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
            </div>
            <div class="Process" v-else-if="pillar.pillarIconSet === 'CFAFE Process'">
              <Process :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
            </div>
            <div class="Facilities" v-else-if="pillar.pillarIconSet === 'CFAFE Facility'">
              <Facilities :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
            </div>
            <div class="EmployeeSurvey" v-else-if="pillar.pillarIconSet === 'CFAFE Survey'">
              <EmployeeSurvey :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}
            </div>
            <div class="Process" v-else><Process :class="pillar.pillarStatus" />{{ pillar.pillarTitle }}</div>
            <Carrot :class="pillar.show ? 'carrot-flipped' : 'carrot'" />
          </div>
          <div v-if="pillar.show" class="pillar-content">
            <div class="pillar">
              <div class="pillar-details" v-for="row in pillar.rows" :key="row.key">
                <div class="pillar-row-details">
                  <div class="pillar-title" v-if="row.pillarRowTitle">{{ row.pillarRowTitle }}:</div>
                  <div class="dial-container" v-for="dialRow in row.dials" :key="dialRow.key">
                    <div class="smmt" v-if="dialRow.dialTypeId === 'SMMT'">
                      <IconProgress
                        :progress="dialRow.smmtProgress"
                        :targets="dialRow.smmtTargets"
                        :color="dialRow.dialColor"
                      />
                      <div class="smmtInner" v-if="dialRow.dialResultText">
                        {{ dialRow.dialResultText }}
                      </div>
                    </div>
                    <div class="smmt" v-else-if="dialRow.dialTypeId === 'FDMT'">
                      <IconProgress
                        :progress="dialRow.smmtProgress"
                        :targets="dialRow.smmtTargets"
                        :color="dialRow.dialColor"
                      />
                      <div class="smmtInner" v-if="dialRow.dialResultText">
                        {{ dialRow.dialResultText }}
                      </div>
                    </div>
                    <div
                      v-else-if="dialRow.dialResultText !== 'EMPTY' && dialRow.dialName !== 'OR'"
                      :class="['circle', dialRow.dialColor]"
                    >
                      <div class="inner" v-if="dialRow.dialResultText">{{ dialRow.dialResultText }}</div>
                      <div class="inner-graphic" v-if="dialRow.dialResultGraphic">
                        <img :class="dialRow.dialResultGraphic" alt="" :src="getDialImage(dialRow.dialResultGraphic)" />
                      </div>
                    </div>
                    <div v-if="dialRow.dialResultText !== 'EMPTY' && dialRow.dialName !== 'OR'" class="dialId">
                      {{ dialRow.dialName }}
                    </div>
                    <div v-if="dialRow.dialName === 'OR'" class="dialId or">{{ dialRow.dialName }}</div>
                  </div>
                </div>
                <template v-for="(dialTables, index) in row.dialData">
                  <div
                    :key="`dialTables-${index}`"
                    :class="['pillar-row-table', dialTables[0].dialId]"
                    v-if="dialTables[0].dialType !== 'SNM'"
                  >
                    <div class="pillar-row-inner" v-for="(dialInfo, index) in dialTables" :key="`object-${index}`">
                      <DialInfo :dialInfo="dialInfo" />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Performance from '@/assets/new/icon_performance.svg';
import Process from '@/assets/new/icon_process.svg';
import Training from '@/assets/new/icon_training.svg';
import EmployeeSurvey from '@/assets/new/icon_employee_survey.svg';
import Facilities from '@/assets/new/icon_facilities.svg';
import Carrot from '@/assets/icons/carrot_up.svg';
import DialInfo from '@/components/DialInfo.vue';
import Periods from '@/components/Periods.vue';
import IconProgress from '@/components/IconProgress.vue';
import VueHtml2pdf from 'vue-html2pdf';
import { mapState } from 'vuex';

export default {
  name: 'Pillar',
  components: {
    Performance,
    Process,
    Training,
    EmployeeSurvey,
    Facilities,
    Carrot,
    DialInfo,
    Periods,
    IconProgress,
    VueHtml2pdf,
  },
  props: {
    pillarDetails: {
      type: Array,
    },
    selectedDealer: {
      type: Object,
    },
  },
  data() {
    return {
      contentRendered: false,
      progress: 0,
      generatingPdf: false,
      pdfDownloaded: false,
    };
  },
  methods: {
    getDialImage(image) {
      if (this.imageTypeCheck(image)) {
        // eslint-disable-next-line import/no-dynamic-require
        return require(`@/assets/new/${image}?external`);
      }
      return null;
    },
    imageTypeCheck(url) {
      return url.match(/\.(jpeg|jpg|gif|png)$/) != null;
    },
    collapseAll(array) {
      array.forEach((pillar) => {
        this.$set(pillar, 'show', false);
      });
    },
    expandAll(array) {
      array.forEach((pillar) => {
        this.$set(pillar, 'show', true);
      });
    },
    generateReport() {
      this.$refs.html2Pdf.generatePdf();
    },
    onProgress(progress) {
      this.progress = progress;
    },
    hasDownloaded() {
      this.pdfDownloaded = true;
    },
  },
  computed: {
    ...mapState('global', ['programStyle']),
    ...mapState(['programBreadcrumb']),
    domain() {
      return this.$route.params.domain;
    },
    entity() {
      return this.$route.params.entity;
    },
    showCollapse() {
      if (this.pillarDetails) {
        const isEven = this.pillarDetails.every((pillar) => pillar.show);
        return isEven;
      }
      return false;
    },
    period() {
      return this.$store.state.activePeriod.period;
    },
  },
  created() {},
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #597dda;
  }
}
.bottom {
  padding: 40px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-content: center;
  .title {
    font-size: 21px;
  }
}
.pillars {
  font-size: 18px;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  .pillar-row {
    border-bottom: 1px solid #dfe4e8;
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    .pillar-row-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      color: black;
      .Performance {
        svg {
          width: 42px !important;
          height: 42px !important;
          padding: 0 10px 5px 10px;
          filter: drop-shadow(0px 3px 6px #00000029);
          &.P {
            g {
              circle {
                fill: green;
              }
            }
          }
        }
      }
      div {
        display: flex;
        align-items: center;
        svg {
          width: 60px !important;
          height: 60px !important;
          filter: drop-shadow(0px 3px 6px #00000029);
        }
      }
      div {
        svg {
          &.P {
            circle {
              fill: green;
            }
          }
        }
      }
    }
    &:last-child {
      border-bottom: none;
      padding-bottom: 0px;
    }
  }
}
.performance-icon {
  width: 42px !important;
  height: 42px !important;
  padding-right: 10px;
  padding-left: 10px;
  filter: drop-shadow(0px 3px 6px #00000029);
  &.P {
    g {
      circle {
        fill: green;
      }
    }
  }
}
.carrot-flipped {
  height: 12px !important;
  cursor: pointer;
  width: 24px !important;
}
.carrot {
  height: 12px !important;
  cursor: pointer;
  width: 24px !important;
  transform: rotate(90deg);
}

.pillar-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  svg {
    height: 60px;
    width: 60px;
  }
  .pillar {
    display: flex;
    flex-direction: column;
    width: 100%;
    svg {
      height: 60px;
      width: 60px;
    }
  }
  .pillar-details {
    padding-left: 100px;
    display: flex;
    flex-direction: column;
    padding-top: 20px;
    &.pdf {
      flex-wrap: wrap;
      flex-direction: column;
    }
  }
  .pillar-row-details {
    display: flex;
    font-size: 18px;
    align-items: center;
    color: #1d1d1b;
    flex-wrap: wrap;
    margin-bottom: 30px;
    .dial-container {
      display: flex;
      align-items: center;
      flex-basis: calc(20% - 20px);
      margin: 5px 15px;
    }
    .dialId {
      text-align: left;
      &.or {
        margin: 0 auto;
      }
    }
    .pillar-title {
      text-align: left;
      width: 100px;
    }
  }
  .pillar-row-table {
    display: flex;
    padding-bottom: 40px;
    border-bottom: 1px solid #dfe4e8;
    margin-bottom: 40px;
    &.pdf {
      padding-bottom: 10px;
      margin-bottom: 10px;
    }
    &.OR {
      display: none;
    }
    .pillar-row-inner {
      display: flex;
    }
    &:last-child {
      border-bottom: none;
      padding-bottom: 0px;
    }
  }
  .header {
    display: flex;
    justify-content: flex-start;
    font-size: 18px;
    font-weight: 100;
    color: #597dda;
    margin-top: 40px;
  }
  .details {
    display: flex;
    margin-top: 17px;
    div {
      display: flex;
      flex-direction: column;
      margin-right: 45px;
      .title {
        color: #1d1d1b;
        font-size: 12px;
      }
      .number {
        font-size: 21px;
        color: #597dda;
      }
    }
  }
}
.SNM {
  display: none !important;
}
.smmtInner {
  top: -48px;
  left: 22px;
}
.smmtInner-pdf {
  display: inline;
  position: relative;
  font-size: 14px;
  top: -45px;
  left: 20px;
}
.pdf-header {
  display: flex;
  justify-content: space-between;
  padding: 20px 10px;
  .right {
    display: flex;
    color: #898c8d;
    flex-direction: column;
    text-align: right;
    justify-content: space-between;
    white-space: nowrap;
  }
  .title {
    font-size: 32px;
    font-weight: 500;
    text-transform: uppercase;
  }
  .left {
    display: flex;
    flex-direction: column;
  }
}
.circle {
  cursor: default;
}
</style>
